import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Sparkles, Shirt, Lock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { useGoogleSignIn } from "@/hooks/useGoogleSignIn";
import { EmailAuthSheet } from "@/components/auth/EmailAuthSheet";

const HEADLINE_FONT = "'Space Grotesk', sans-serif";
const BODY_FONT = "'Manrope', sans-serif";

/**
 * Native Android Welcome / Auth screen — matches the Stitch design exactly.
 * Email button currently routes to the same Google flow placeholder; can be
 * wired to a magic-link form later without touching this layout.
 */
const NativeWelcomeAuth = () => {
  const { signingIn, signInWithGoogle } = useGoogleSignIn();
  const [emailOpen, setEmailOpen] = useState(false);

  useEffect(() => {
    const fontId = "stitch-fonts";
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700;900&family=Manrope:wght@300;400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <>
      <SEO />
      <div
        className="dark relative flex min-h-[100dvh] flex-col overflow-hidden bg-[#0e0e11] text-[#e7e4ec]"
        style={{
          fontFamily: BODY_FONT,
          paddingTop: "env(safe-area-inset-top)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {/* Ambient background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -left-32 -top-24 h-[420px] w-[420px] rounded-full bg-[#ac8aff]/20 blur-[140px]" />
          <div className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#ec4899]/15 blur-[140px]" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #ac8aff 1px, transparent 1px), linear-gradient(to bottom, #ac8aff 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />
        </div>

        {/* Hero / brand block */}
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-6 pt-16 text-center">
          <div className="mb-7 flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl shadow-[0_0_40px_-6px_#ac8aff]">
            <img
              src="/app-icon-128.png"
              alt="FitAura AI"
              className="h-full w-full object-cover"
              width={80}
              height={80}
              loading="eager"
              decoding="async"
              fetchPriority="high"
            />
          </div>

          <h1
            className="bg-gradient-to-r from-[#ac8aff] via-[#d4a5ff] to-[#00daf3] bg-clip-text text-4xl font-black uppercase tracking-[0.18em] text-transparent"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            FitAura AI
          </h1>

          <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-[#acaab1]">
            Try on any outfit. Score your aura. Style yourself with AI.
          </p>

          {/* Social proof chips */}
          <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#e7e4ec] backdrop-blur-md"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <Sparkles className="h-3 w-3 fill-[#ffd35e] text-[#ffd35e]" />
              AI Powered
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#e7e4ec] backdrop-blur-md"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <Shirt className="h-3 w-3 text-[#ac8aff]" />
              Instant Try-On
            </span>
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#e7e4ec] backdrop-blur-md"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              <Lock className="h-3 w-3 text-[#00daf3]" />
              Private
            </span>
          </div>
        </div>

        {/* Action block */}
        <div className="relative z-10 px-6 pb-6">
          <Button
            type="button"
            onClick={signInWithGoogle}
            disabled={signingIn}
            className="h-14 w-full rounded-2xl bg-gradient-to-r from-[#ac8aff] to-[#8f60fa] text-[13px] font-bold uppercase tracking-[0.22em] text-[#0e0e11] shadow-[0_0_30px_-6px_#ac8aff] hover:from-[#bfa5ff] hover:to-[#ac8aff] active:scale-[0.98] disabled:opacity-80"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            {signingIn ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Opening Google…
              </>
            ) : (
              <>
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    fill="#0e0e11"
                    d="M21.35 11.1H12v3.2h5.35c-.23 1.45-1.7 4.25-5.35 4.25-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.83 0 3.06.78 3.76 1.45l2.56-2.46C16.7 4.16 14.55 3.2 12 3.2 6.96 3.2 2.9 7.26 2.9 12.3s4.06 9.1 9.1 9.1c5.25 0 8.73-3.69 8.73-8.88 0-.6-.07-1.06-.18-1.42z"
                  />
                </svg>
                Continue with Google
              </>
            )}
          </Button>

          {/* Divider */}
          <div className="my-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span
              className="text-[9px] font-bold uppercase tracking-[0.3em] text-[#76757b]"
              style={{ fontFamily: HEADLINE_FONT }}
            >
              or
            </span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* Email auth — Android-only entry point */}
          <Button
            type="button"
            variant="outline"
            onClick={() => setEmailOpen(true)}
            className="h-14 w-full rounded-2xl border-white/15 bg-white/[0.04] text-[13px] font-bold uppercase tracking-[0.22em] text-white hover:bg-white/[0.08] active:scale-[0.98]"
            style={{ fontFamily: HEADLINE_FONT }}
          >
            <Mail className="mr-2 h-4 w-4 text-[#ac8aff]" />
            Continue with Email
          </Button>

          <p className="mt-5 text-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#76757b]">
            By continuing you agree to our{" "}
            <Link to="/terms" className="text-[#bfa5ff] underline">
              Terms
            </Link>{" "}
            &{" "}
            <Link to="/privacy" className="text-[#bfa5ff] underline">
              Privacy
            </Link>
          </p>
        </div>

        <EmailAuthSheet open={emailOpen} onClose={() => setEmailOpen(false)} />
      </div>
    </>
  );
};

export default NativeWelcomeAuth;

